import { observer } from 'mobx-react';
import React, { useEffect, useRef } from 'react';
import { FormProvider } from 'react-hook-form';
import { useLocation } from 'react-router-dom';
import {
    MainScreen,
    OverContentLoadableBlock,
    PageFrame,
    ShowComponent,
    Step,
    useForm,
    useNavigate,
} from '@alfa-retail-react/components';
import Button from '@alfa-retail-react/components/button';
import YesNoQuestion, {
    YesNoState,
} from '@alfa-retail-react/components/yes-no-question';

import {
    AgreementTermsLink,
    MarketingCommunicationLink,
} from 'app/constants/links';
import {
    unauthorizedExceptionHandlerOnLoadData,
    unauthorizedExceptionHandlerOnSubmitData,
} from 'app/logic/pages/error/unauthorizedExceptionHandler';
import { validationExceptionHandler } from 'app/logic/pages/error/validationExceptionHandler';
import { AnalyticsActionTypeName } from 'app/analytics/AnalyticsActionTypeName';
import { useAnalytics } from 'app/analytics/useAnalytics';
import { useApplicationContext } from 'app/store/сontext/ApplicationContext';
import { OrderSkeleton } from 'app/component/page/skeletons';
import { StepOrder, TOTAL_STEP_COUNT } from 'app/constants/step';
import { AddressForm, AddressFormValues } from 'app/logic/address/AddressForm';
import {
    CONDITION_AGREEMENT_FIELD_NAME,
    ConditionAgreementFormItem,
    MARKETING_COMMUNICATION_FIELD_NAME,
} from 'app/logic/ConditionAgreementFormItem';
import { useNotificationContext } from 'app/store/сontext/NotificationContextProvider';
import { defaultExceptionHandler } from 'app/logic/pages/error/defaultExceptionHandler';
import { exceptionLogger } from 'app/logic/pages/error/exceptionLogger';
import {
    networkExceptionHandlerOnLoadData,
    networkExceptionHandlerOnSubmitData,
} from 'app/logic/pages/error/networkExceptionHandler';
import { orderRejectedExceptionHandler } from 'app/logic/pages/error/orderRejectedExceptionHandler';
import { PageErrors } from 'app/logic/pages/error/PageErrors';
import { BirthPlaceFormSection } from 'app/logic/pages/order/BirthPlaceFormSection';
import { FatcaFormItem } from 'app/logic/pages/order/FatcaFormItem';
import 'app/logic/pages/order/OrderPage.less';
import { OrderPageStore } from 'app/logic/pages/order/OrderPageStore';
import { PersonalDataFieldSet } from 'app/logic/pages/order/PersonalDataFieldSet';
import { PersonalDataMissingForm } from 'app/logic/pages/order/PersonalDataMissingForm';
import { RegistrationAddressFormSection } from 'app/logic/pages/order/RegistrationAddressFormSection';
import { WrongData } from 'app/logic/pages/order/WrongData';
import { CardOrder } from 'app/model/CardOrder';
import { Client } from 'app/model/Client';
import { LocalityReference } from 'app/model/LocalityReference';
import { Reference } from 'app/model/Reference';
import { PageNames } from 'app/PageNames';
import { OrderService } from 'app/service/OrderService';
import { StepNames } from 'app/StepNames';
import { useI18nContext } from 'app/store/сontext/I18nContext';
import {
    MARKETING_CONSENT_FORM_ITEM_NAME,
    MarketingConsentFormItem,
} from './MarketingConsentFormItem';
import { MarketingConsent } from 'app/model/MarketingConsent';

export type OrderFormValues = {
    surnameLatin: string;
    nameLatin: string;
    secretWord: string;
    email: string;
    cardholderName: string;

    birthCountryCode: string;
    birthLocalityType: Reference;
    birthLocality: string;

    belarusLocality: LocalityReference;
    registrationAddress: AddressFormValues;
    residentialAddress: AddressFormValues;

    fatca: YesNoState;
    [CONDITION_AGREEMENT_FIELD_NAME]: boolean;
    [MARKETING_COMMUNICATION_FIELD_NAME]: boolean;
    [MARKETING_CONSENT_FORM_ITEM_NAME]: MarketingConsent[];
};

const CURRENT_STEP_ORDER = StepOrder.STEP_PERSONAL_DATA;
const PAGE_NAME = 'PersonalDataPage';

type OrderPageState = {
    /**
     * Идентификатор предыдущей страницы
     */
    refererPage: PageNames;

    /**
     * Определяет, продолжает ли пользователь ранее созданную заявку
     * (черновик), или создает новую
     * @private
     */
    isDraftContinuation: boolean;
};

export const OrderPage = observer(() => {
    const navigate = useNavigate();
    const location = useLocation();
    const applicationContext = useApplicationContext();
    const i18nContext = useI18nContext();
    const { refererPage, isDraftContinuation } =
        location.state as OrderPageState;

    /**
     * проверки на случай перехода назад по приложению
     */
    const isFromContinueOrderPage =
        refererPage === PageNames.DRAFT_ORDER_PAGE_NAME ||
        refererPage === PageNames.CONTINUE_DRAFT_PAGE_NAME;
    const shouldPersonalizeOrder = isFromContinueOrderPage;
    const shouldShowDraftNotification =
        isFromContinueOrderPage && isDraftContinuation;

    const pageStore = useRef(
        new OrderPageStore(shouldPersonalizeOrder),
    ).current;
    const { order, orderFormStore, loadStateStore } = pageStore;

    const analytics = useAnalytics(
        applicationContext.productCode,
        PAGE_NAME,
        false,
    );

    const notification = useNotificationContext();

    useEffect(() => {
        analytics.pageDisplayed();

        i18nContext
            .initialize()
            .catch(
                unauthorizedExceptionHandlerOnLoadData({
                    navigate,
                    phoneNumber: applicationContext.phoneNumber,
                }),
            )
            .catch(exceptionLogger(PAGE_NAME, 'i18nContext.initialize'))
            .catch(networkExceptionHandlerOnLoadData({ navigate }));

        const orderId = sessionStorage.getItem('orderId');

        pageStore
            .initialize(orderId)
            .then(() => {
                if (shouldPersonalizeOrder) {
                    applicationContext.phoneNumber =
                        pageStore.order.client.mobilePhone;

                    analytics.resolve(AnalyticsActionTypeName.PERSONALIZATION);
                }
            })
            .catch(
                unauthorizedExceptionHandlerOnLoadData({
                    navigate,
                    phoneNumber: applicationContext.phoneNumber,
                }),
            )
            .catch(exceptionLogger(PAGE_NAME, 'pageStore.initialize'))
            .catch(
                orderRejectedExceptionHandler(
                    navigate,
                    analytics,
                    i18nContext.t,
                ),
            )
            .catch(networkExceptionHandlerOnLoadData({ navigate }));
    }, []);

    useEffect(() => {
        if (loadStateStore.isLoaded && shouldShowDraftNotification) {
            notification.addNotification({
                title: 'Вы можете изменить данные или нажать кнопку “Подтвердить”',
                badge: 'attention',
            });
        }
    }, [loadStateStore.isLoaded]);

    const form = useForm<OrderFormValues>({
        errorContextConverter: pageStore.contextConverter,
    });

    const {
        handleSubmitWithValidationError,
        pageErrors,
        setPageErrors,
        pageErrorsComponentRef,
    } = form;

    const onSubmitForm = () => {
        const registrationAddress =
            orderFormStore.registrationAddress.getModel();

        const residentialAddress = orderFormStore.notSameAddress
            ? orderFormStore.residentialAddress.getModel()
            : orderFormStore.registrationAddress.getModel();

        const client = new Client(
            order.client.surname,
            orderFormStore.surnameLatin,
            order.client.name,
            orderFormStore.nameLatin,
            order.client.patronymic,
            order.client.birthDate,
            orderFormStore.birthPlaceFormStore.getModel(),
            order.client.sex,
            order.client.identity,
            order.client.citizenshipCountryCode,
            order.client.residencyCountryCode,
            order.client.mobilePhone,
            orderFormStore.email,
            registrationAddress,
            residentialAddress,
            orderFormStore.isUnderFatca,
            order.client.employment,
            orderFormStore.secretWord,
            orderFormStore.isMarketingConsentSigned,
            orderFormStore.marketingConsentList,
        );

        const orderCopy = new CardOrder(
            order.id,
            order.saleChannel,
            client,
            order.cardProductCode,
            order.cardProductId,
            order.insuranceProductCode,
            order.amount,
            order.currency,
            order.deliveryAddress,
            order.authenticationTimestamp,
            order.stage,
            orderFormStore.cardholderName,
            order.state,
            order.number,
            order.source,
        );

        orderCopy.stage = StepNames.STEP_PERSONAL_DATA;

        if (!orderCopy.client.isMarketingConsentSigned) {
            orderCopy.client.marketingConsentList = null;
        }

        //todo чекнуть, зависит ли обработка ошибки валидации от обертки pageStore.actionStateStore.loadData
        return pageStore.actionStateStore.loadData(() => {
            analytics.confirm();
            return new OrderService()
                .updateOrder(orderCopy)
                .then(() => {
                    applicationContext.latestSubmittedStep =
                        StepOrder.STEP_PERSONAL_DATA;
                    navigate(`/${applicationContext.productCode}/employment`);
                })
                .catch(exceptionLogger(PAGE_NAME, 'orderService.updateOrder'))
                .catch(
                    unauthorizedExceptionHandlerOnSubmitData({
                        navigate,
                        phoneNumber: applicationContext.phoneNumber,
                    }),
                )
                .catch(
                    orderRejectedExceptionHandler(
                        navigate,
                        analytics,
                        i18nContext.t,
                    ),
                )
                .catch(
                    validationExceptionHandler(
                        form,
                        analytics,
                        pageStore.contextConverter,
                    ),
                )
                .catch(networkExceptionHandlerOnSubmitData({ setPageErrors }))
                .catch(defaultExceptionHandler(notification, analytics));
        });
    };

    const isShowMarketingConsent =
        !pageStore.order?.client.isMarketingConsentSigned;

    return (
        <OverContentLoadableBlock show={pageStore.actionStateStore.isLoading}>
            <PageFrame
                rightAddonLogo={applicationContext.additionalLogo}
                displayFooter
            >
                <MainScreen>
                    <MainScreen.Header
                        title={applicationContext.getCardProductTitle()}
                    />
                    <MainScreen.ContentBar>
                        <ShowComponent
                            condition={loadStateStore.isLoaded}
                            alternative={<OrderSkeleton />}
                        >
                            <div className="orderPage">
                                <Step
                                    current={CURRENT_STEP_ORDER}
                                    total={TOTAL_STEP_COUNT}
                                />

                                <PersonalDataFieldSet
                                    order={pageStore.order}
                                    documentType={pageStore.documentType}
                                    residencyCountryReference={
                                        pageStore.residencyCountryReference
                                    }
                                    citizenshipCountryReference={
                                        pageStore.citizenshipCountryReference
                                    }
                                />
                                <WrongData
                                    order={pageStore.order}
                                    analytics={analytics}
                                    onCancelOrder={pageStore.cancelOrder}
                                />
                                <FormProvider<OrderFormValues> {...form}>
                                    <form
                                        onSubmit={handleSubmitWithValidationError(
                                            {
                                                onValid: onSubmitForm,
                                            },
                                        )}
                                    >
                                        <PersonalDataMissingForm
                                            pageStore={pageStore}
                                        />

                                        {loadStateStore.isLoaded && (
                                            <>
                                                <BirthPlaceFormSection
                                                    pageStore={pageStore}
                                                />
                                                <RegistrationAddressFormSection
                                                    pageStore={pageStore}
                                                />
                                                <YesNoQuestion
                                                    className={'sameAddress'}
                                                    question={
                                                        'Адрес регистрации совпадает с адресом проживания?'
                                                    }
                                                    value={
                                                        orderFormStore.sameAddress
                                                    }
                                                    fields={[
                                                        {
                                                            key: YesNoState.YES,
                                                            title: 'Да',
                                                        },
                                                        {
                                                            key: YesNoState.NO,
                                                            title: 'Нет',
                                                        },
                                                    ]}
                                                    onChange={(event, value) =>
                                                        orderFormStore.setSameAddress(
                                                            value,
                                                        )
                                                    }
                                                />
                                                {orderFormStore.notSameAddress && (
                                                    <>
                                                        <h4 className="residentialAddressFormTitle">
                                                            Адрес проживания
                                                        </h4>
                                                        <AddressForm<
                                                            OrderFormValues,
                                                            unknown
                                                        >
                                                            className="residential"
                                                            prefix={
                                                                'residentialAddress'
                                                            }
                                                            formStore={
                                                                orderFormStore.residentialAddress
                                                            }
                                                        />
                                                    </>
                                                )}
                                                <FatcaFormItem
                                                    orderFormStore={
                                                        pageStore.orderFormStore
                                                    }
                                                />
                                                <ConditionAgreementFormItem<
                                                    OrderFormValues,
                                                    unknown
                                                >
                                                    name={
                                                        CONDITION_AGREEMENT_FIELD_NAME
                                                    }
                                                    checked={
                                                        pageStore.conditionAgreement
                                                    }
                                                    defaultValue={false}
                                                    label={
                                                        <>
                                                            Я подтверждаю
                                                            достоверность и
                                                            актуальность, а
                                                            также даю{' '}
                                                            <AgreementTermsLink title="согласие" />{' '}
                                                            на обработку
                                                            указанных данных
                                                        </>
                                                    }
                                                    validationErrorMessage={
                                                        'Ваше согласие обязательно'
                                                    }
                                                    onChange={(checked) =>
                                                        (pageStore.conditionAgreement =
                                                            checked)
                                                    }
                                                />
                                                {isShowMarketingConsent && (
                                                    <MarketingConsentFormItem
                                                        checked={
                                                            orderFormStore.isMarketingConsentSigned
                                                        }
                                                        orderFormStore={
                                                            orderFormStore
                                                        }
                                                    />
                                                )}
                                            </>
                                        )}
                                        {pageErrors.length > 0 && (
                                            <PageErrors
                                                ref={
                                                    pageErrorsComponentRef as React.Ref<HTMLDivElement>
                                                }
                                                errors={pageErrors}
                                            />
                                        )}
                                        <Button
                                            className="submitAction"
                                            type="submit"
                                            view="accent"
                                            disabled={
                                                pageStore.actionStateStore
                                                    .isLoading
                                            }
                                        >
                                            Подтвердить
                                        </Button>
                                    </form>
                                </FormProvider>
                            </div>
                        </ShowComponent>
                    </MainScreen.ContentBar>
                </MainScreen>
            </PageFrame>
        </OverContentLoadableBlock>
    );
});
